import React, { useState, useEffect } from 'react';
import { HealthStatusProvider } from '../utils/HealthStatusProvider';
import type { M365ServiceStatus } from '../types/m365';
import './M365ServiceStatusGrid.css';

const GREEN_STATUS = new Set([0, 4, 5, 11, 14]);
const YELLOW_STATUS = new Set([6, 7, 12, 13, 15, 16]);

function getStatusIconClass(statusValue: number): { icon: string; color: string } {
    if (GREEN_STATUS.has(statusValue)) {
        return { icon: 'bi-check-circle-fill', color: 'var(--m365-status-green, #198754)' };
    }
    if (YELLOW_STATUS.has(statusValue)) {
        return { icon: 'bi-exclamation-triangle-fill', color: 'var(--m365-status-yellow, #ffc107)' };
    }
    return { icon: 'bi-x-circle-fill', color: 'var(--m365-status-red, #dc3545)' };
}

function parseStatus(status: string | number | unknown): number {
    if (typeof status === 'number' && !Number.isNaN(status)) return status;
    if (typeof status === 'string') {
        const n = parseInt(status, 10);
        return Number.isNaN(n) ? -1 : n;
    }
    return -1;
}

export default function M365ServiceStatusGrid() {
    const [items, setItems] = useState<M365ServiceStatus[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        HealthStatusProvider.getInstance()
            .GetM365ServiceStatus()
            .then((data) => {
                if (!cancelled) setItems(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load service status');
                    setItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <div className="m365-service-status-grid loading d-flex align-items-center justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="m365-service-status-grid error alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className="m365-service-status-grid empty text-muted text-center py-5">
                No service status data available.
            </div>
        );
    }

    return (
        <div className="m365-service-status-grid">
            <div className="m365-service-status-grid__container">
                {items.map((item, index) => {
                    const statusNum = parseStatus(item.Status);
                    const { icon, color } = getStatusIconClass(statusNum);
                    return (
                        <div
                            key={item.Id || `${item.Service}-${index}`}
                            className="m365-service-status-grid__card"
                            style={{ animationDelay: `${index * 0.05}s` }}
                        >
                            <div className="m365-service-status-grid__card-inner">
                                <div
                                    className="m365-service-status-grid__status"
                                    style={{ color }}
                                    title={`Status: ${item.Status}`}
                                    aria-label={`Status: ${item.Status}`}
                                >
                                    <i className={`bi ${icon}`} />
                                </div>
                                <div className="m365-service-status-grid__service" title={item.Service}>
                                    {item.Service}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
